/**
 * File discovery service for @ symbol file input functionality
 * Provides file search, auto-completion, and path resolution capabilities
 */

import api from './api'

export interface FileDiscoveryItem {
  name: string
  path: string
  type: 'file' | 'directory'
  size?: number
  extension?: string
  relativePath: string
}

export interface FileSearchOptions {
  searchTerm: string
  maxResults?: number
  includeDirectories?: boolean
  fileExtensions?: string[]
  conversationId?: string
}

export interface FileAutoCompleteResult {
  items: FileDiscoveryItem[]
  hasMore: boolean
  searchTerm: string
}

class FileDiscoveryService {
  private cache = new Map<string, { data: FileDiscoveryItem[]; timestamp: number }>()
  private readonly CACHE_TTL = 30000 // 30 seconds

  /**
   * Parse @ command from input text
   * Supports patterns like: @file.txt, @folder/, @path/to/file.js
   */
  parseAtCommand(input: string): { prefix: string; atCommand: string; suffix: string } | null {
    // Find @ symbol that's either at start or preceded by whitespace
    const atIndex = input.search(/(?:^|\s)@/)
    if (atIndex === -1) return null

    // Find the actual @ position (accounting for possible leading whitespace)
    const actualAtIndex = input.indexOf('@', atIndex)
    if (actualAtIndex === -1) return null

    // Extract everything after @ until next whitespace or end
    const afterAt = input.substring(actualAtIndex + 1)
    const spaceIndex = afterAt.search(/\s/)
    const pathPart = spaceIndex === -1 ? afterAt : afterAt.substring(0, spaceIndex)

    return {
      prefix: input.substring(0, actualAtIndex),
      atCommand: `@${pathPart}`,
      suffix: spaceIndex === -1 ? '' : afterAt.substring(spaceIndex)
    }
  }

  /**
   * Get auto-completion suggestions for @ commands
   */
  async getAutoCompleteSuggestions(
    input: string,
    options: { conversationId?: string; maxResults?: number } = {}
  ): Promise<FileAutoCompleteResult> {
    const parsed = this.parseAtCommand(input)
    if (!parsed) {
      return { items: [], hasMore: false, searchTerm: '' }
    }

    // Extract path from @command (remove @ prefix)
    const searchPath = parsed.atCommand.substring(1)
    const searchTerm = searchPath

    // Determine if this is a directory search or file search
    const isDirectorySearch = searchPath.endsWith('/')
    const searchDirectory = isDirectorySearch ? searchPath : this.getDirectoryFromPath(searchPath)
    const filePrefix = isDirectorySearch ? '' : this.getFileNameFromPath(searchPath)

    try {
      let items: FileDiscoveryItem[] = []

      if (searchPath === '' || searchPath.length < 2) {
        // Get root directory contents
        items = await this.getDirectoryContents('.', options.conversationId)
      } else if (isDirectorySearch || searchDirectory !== '.') {
        // Get specific directory contents
        items = await this.getDirectoryContents(searchDirectory, options.conversationId)
      } else {
        // Search for files matching the prefix
        items = await this.searchFiles(filePrefix, '.', options.conversationId)
      }

      // Filter by file prefix if specified
      if (filePrefix && !isDirectorySearch) {
        items = items.filter(item => 
          item.name.toLowerCase().startsWith(filePrefix.toLowerCase())
        )
      }

      // Sort: directories first, then files, alphabetically within each group
      items.sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1
        }
        return a.name.localeCompare(b.name)
      })

      // Limit results
      const maxResults = options.maxResults || 20
      const hasMore = items.length > maxResults
      if (hasMore) {
        items = items.slice(0, maxResults)
      }

      return {
        items,
        hasMore,
        searchTerm
      }

    } catch (error) {
      console.error('Error getting auto-complete suggestions:', error)
      return { items: [], hasMore: false, searchTerm }
    }
  }

  /**
   * Get directory contents with caching
   */
  private async getDirectoryContents(
    dirPath: string, 
    conversationId?: string
  ): Promise<FileDiscoveryItem[]> {
    const cacheKey = `${dirPath}:${conversationId || 'default'}`
    const cached = this.cache.get(cacheKey)
    
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data
    }

    try {
      // Use the existing filesystem API with conversation context
      const params = conversationId ? `?conversationId=${conversationId}` : ''
      const cleanPath = dirPath === '.' ? '' : dirPath
      const encodedPath = cleanPath ? encodeURIComponent(cleanPath) : ''
      const url = encodedPath ? `/filesystem/browse/${encodedPath}${params}` : `/filesystem/browse/${params}`
      
      const response = await api.get(url)
      
      if (!response.data || !response.data.files) {
        return []
      }

      const items: FileDiscoveryItem[] = response.data.files.map((file: any) => ({
        name: file.name,
        path: file.path,
        type: file.type,
        size: file.size,
        extension: file.extension,
        relativePath: file.path
      }))

      // Cache the results
      this.cache.set(cacheKey, { data: items, timestamp: Date.now() })
      
      return items
    } catch (error) {
      console.error(`Error loading directory ${dirPath}:`, error)
      return []
    }
  }

  /**
   * Search for files recursively (simple implementation)
   */
  private async searchFiles(
    searchTerm: string,
    basePath: string,
    conversationId?: string
  ): Promise<FileDiscoveryItem[]> {
    try {
      // For now, just search in current directory
      // In the future, this could be enhanced to do recursive search
      const items = await this.getDirectoryContents(basePath, conversationId)
      
      return items.filter(item => 
        item.type === 'file' && 
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    } catch (error) {
      console.error('Error searching files:', error)
      return []
    }
  }

  /**
   * Get file content for @ reference
   */
  async getFileContent(filePath: string, _conversationId?: string): Promise<{
    content: string
    filename: string
    extension: string
    size: number
  } | null> {
    try {
      const response = await api.get(`/filesystem/read/${encodeURIComponent(filePath)}`)
      
      return {
        content: response.data.content,
        filename: this.getFileNameFromPath(filePath),
        extension: response.data.extension,
        size: response.data.size
      }
    } catch (error) {
      console.error(`Error reading file ${filePath}:`, error)
      return null
    }
  }

  /**
   * Validate if a path exists and is accessible
   */
  async validatePath(filePath: string, _conversationId?: string): Promise<boolean> {
    try {
      await api.get(`/filesystem/info/${encodeURIComponent(filePath)}`)
      return true
    } catch (error) {
      return false
    }
  }

  /**
   * Escape spaces in file paths for @ commands
   */
  escapePath(filePath: string): string {
    return filePath.replace(/ /g, '\\ ')
  }

  /**
   * Unescape spaces in file paths
   */
  unescapePath(filePath: string): string {
    return filePath.replace(/\\ /g, ' ')
  }

  /**
   * Get directory portion of a file path
   */
  private getDirectoryFromPath(filePath: string): string {
    const lastSlash = filePath.lastIndexOf('/')
    if (lastSlash === -1) return '.'
    if (lastSlash === 0) return '/'
    return filePath.substring(0, lastSlash)
  }

  /**
   * Get filename portion of a file path
   */
  private getFileNameFromPath(filePath: string): string {
    const lastSlash = filePath.lastIndexOf('/')
    return lastSlash === -1 ? filePath : filePath.substring(lastSlash + 1)
  }

  /**
   * Clear cache (useful for testing or when files change)
   */
  clearCache(): void {
    this.cache.clear()
  }

  /**
   * Check if a file type is supported for @ references
   */
  isFileTypeSupported(extension: string): boolean {
    const supportedExtensions = [
      // Text files
      'txt', 'md', 'markdown', 'rst', 'json', 'yaml', 'yml', 'xml', 'csv',
      // Code files
      'js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'h', 'cs',
      'php', 'rb', 'go', 'rs', 'swift', 'kt', 'scala', 'clj',
      // Web files
      'html', 'css', 'scss', 'sass', 'less',
      // Config files
      'ini', 'conf', 'config', 'env', 'gitignore', 'dockerignore',
      // Documentation
      'README', 'LICENSE', 'CHANGELOG'
    ]
    
    return supportedExtensions.includes(extension.toLowerCase())
  }
}

// Create singleton instance
export const fileDiscoveryService = new FileDiscoveryService()
export default fileDiscoveryService